import { useEffect, useRef, useState, useCallback } from 'react';
import type { Socket } from 'socket.io-client';

interface TranscriptSegment {
  text: string;
  timestamp: string;
  speaker: string;
}

interface UseRealtimeTranscriptionProps {
  roomId: string;
  socketRef: React.MutableRefObject<Socket | null>;
  localStreamRef: React.MutableRefObject<MediaStream | null>;
  remoteStreamRef?: React.MutableRefObject<MediaStream | null>; // Optional remote stream for both-party transcription
  enabled: boolean; // Only transcribe when call is active
}

export function useRealtimeTranscription({
  roomId,
  socketRef,
  localStreamRef,
  remoteStreamRef,
  enabled
}: UseRealtimeTranscriptionProps) {
  const [transcript, setTranscript] = useState<TranscriptSegment[]>([]);
  const [isTranscribing, setIsTranscribing] = useState(false);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);

  // Start capturing audio and streaming to backend
  const startTranscription = useCallback(async () => {
    if (!localStreamRef.current || !socketRef.current || isTranscribing) {
      console.log('❌ Cannot start transcription:', {
        hasStream: !!localStreamRef.current,
        hasSocket: !!socketRef.current,
        isTranscribing
      });
      return;
    }

    try {
      console.log('🎤 Starting real-time transcription...');

      // ✅ FIX: Mix both local and remote audio for complete conversation transcription
      const audioTracks: MediaStreamTrack[] = [];

      // Add local audio (user's microphone)
      if (localStreamRef.current) {
        const localAudioTracks = localStreamRef.current.getAudioTracks();
        audioTracks.push(...localAudioTracks);
        console.log(`🎤 Added ${localAudioTracks.length} local audio track(s)`);
      }

      // Add remote audio (other participant's voice)
      if (remoteStreamRef?.current) {
        const remoteAudioTracks = remoteStreamRef.current.getAudioTracks();
        audioTracks.push(...remoteAudioTracks);
        console.log(`🔊 Added ${remoteAudioTracks.length} remote audio track(s)`);
      }

      if (audioTracks.length === 0) {
        console.error('❌ No audio tracks available for transcription');
        return;
      }

      // Create audio context to mix the tracks
      const audioContext = new AudioContext();
      const destination = audioContext.createMediaStreamDestination();

      // Mix all audio tracks together
      audioTracks.forEach((track, index) => {
        const source = audioContext.createMediaStreamSource(new MediaStream([track]));
        source.connect(destination);
        console.log(`🔀 Mixed audio track ${index + 1}: ${track.label || track.kind}`);
      });

      const audioStream = destination.stream;

      console.log('🎵 Mixed audio stream created:', {
        trackCount: audioStream.getAudioTracks().length,
        localTracks: localStreamRef.current?.getAudioTracks().length || 0,
        remoteTracks: remoteStreamRef?.current?.getAudioTracks().length || 0
      });

      // Try different codecs for compatibility
      let mimeType = 'audio/webm;codecs=opus';
      if (!MediaRecorder.isTypeSupported(mimeType)) {
        console.warn('⚠️ webm/opus not supported, trying webm...');
        mimeType = 'audio/webm';
        if (!MediaRecorder.isTypeSupported(mimeType)) {
          console.warn('⚠️ webm not supported, using default...');
          mimeType = '';
        }
      }

      // ✅ FIX: Add explicit high-quality audio settings for better transcription
      const options: MediaRecorderOptions = mimeType
        ? {
            mimeType,
            audioBitsPerSecond: 128000  // 128 kbps for high quality speech (2-3x better than default)
          }
        : {
            audioBitsPerSecond: 128000  // Apply even without explicit mimeType
          };

      console.log('🎙️ Using MediaRecorder with:', options);
      const mediaRecorder = new MediaRecorder(audioStream, options);

      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      // ✅ NEW: Log actual audio quality settings after MediaRecorder initialization
      mediaRecorder.onstart = () => {
        const audioTrack = audioStream.getAudioTracks()[0];
        if (audioTrack) {
          const settings = audioTrack.getSettings();
          console.log('🎤 ═══════════════════════════════════════════════════');
          console.log('🎤 AUDIO CAPTURE QUALITY VERIFICATION');
          console.log('🎤 ═══════════════════════════════════════════════════');
          console.log('🎤 Audio Track Settings:', {
            sampleRate: settings.sampleRate || 'not specified',
            sampleSize: settings.sampleSize || 'not specified',
            channelCount: settings.channelCount || 'not specified',
            echoCancellation: settings.echoCancellation,
            noiseSuppression: settings.noiseSuppression,
            autoGainControl: settings.autoGainControl,
            deviceId: settings.deviceId || 'default',
            label: audioTrack.label || 'unknown'
          });
          console.log('🎙️ MediaRecorder Settings:', {
            mimeType: mediaRecorder.mimeType,
            audioBitsPerSecond: mediaRecorder.audioBitsPerSecond || 'browser default',
            state: mediaRecorder.state
          });
          console.log('🎤 ═══════════════════════════════════════════════════');

          // Warn if settings are suboptimal
          if (settings.sampleRate && settings.sampleRate < 44100) {
            console.warn('⚠️ WARNING: Sample rate is below 44.1 kHz - transcription quality may be affected');
          }
          if (!mediaRecorder.audioBitsPerSecond || mediaRecorder.audioBitsPerSecond < 64000) {
            console.warn('⚠️ WARNING: Audio bitrate is below 64 kbps - transcription quality may be affected');
          }
        }
      };

      // ✅ FIX: Manually stop/restart MediaRecorder every 30 seconds using a timer
      // This creates complete WebM files with proper headers each time
      const CHUNK_DURATION_MS = 30000; // 30 seconds

      mediaRecorder.ondataavailable = async (event) => {
        if (event.data.size === 0) {
          console.warn('⚠️ No audio data in chunk');
          return;
        }

        const MIN_CHUNK_SIZE = 50 * 1024; // 50KB minimum
        if (event.data.size < MIN_CHUNK_SIZE) {
          console.warn(`⚠️ Audio chunk too small (${event.data.size} bytes), skipping`);
          return;
        }

        console.log(`🎵 Sending complete WebM file: ${event.data.size} bytes`);

        // Convert to base64 and send immediately
        const reader = new FileReader();
        reader.onloadend = () => {
          const base64Audio = reader.result?.toString().split(',')[1];

          if (base64Audio && socketRef.current) {
            console.log(`📤 Sending audio to backend:`, {
              roomId,
              blobSize: event.data.size,
              base64Length: base64Audio.length,
              socketConnected: socketRef.current.connected
            });

            socketRef.current.emit('audio-chunk', {
              roomId,
              audioData: base64Audio,
              timestamp: Date.now()
            });

            console.log(`✅ Audio sent successfully`);
          } else {
            console.error('❌ Cannot send audio:', {
              hasBase64: !!base64Audio,
              hasSocket: !!socketRef.current
            });
          }
        };
        reader.readAsDataURL(event.data);
      };

      mediaRecorder.onstop = async () => {
        console.log('🛑 MediaRecorder stopped');
      };

      // Start initial recording
      mediaRecorder.start();
      setIsTranscribing(true);
      console.log('📼 MediaRecorder started');

      // Set up interval to stop and restart every 30 seconds
      const captureInterval = setInterval(() => {
        if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
          console.log('⏸️ Stopping MediaRecorder to finalize WebM file...');
          mediaRecorderRef.current.stop(); // This triggers ondataavailable with complete WebM

          // Restart after a brief delay to let the stop complete
          setTimeout(() => {
            if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'inactive') {
              mediaRecorderRef.current.start();
              console.log('🔄 MediaRecorder restarted for next chunk');
            }
          }, 100);
        }
      }, CHUNK_DURATION_MS);

      console.log(`⏰ Set up timer to capture complete WebM files every ${CHUNK_DURATION_MS}ms`);

      // Store interval for cleanup
      (mediaRecorder as any).captureInterval = captureInterval;

      console.log('✅ Transcription started');
    } catch (error) {
      console.error('Error starting transcription:', error);
      setIsTranscribing(false);
    }
  }, [localStreamRef, socketRef, roomId, isTranscribing]);

  // Stop transcription
  const stopTranscription = useCallback(() => {
    if (mediaRecorderRef.current) {
      const mediaRecorder = mediaRecorderRef.current;

      // Clear interval
      const interval = (mediaRecorder as any).captureInterval;
      if (interval) {
        clearInterval(interval);
      }

      // Stop recording
      if (mediaRecorder.state === 'recording') {
        mediaRecorder.stop();
      }

      mediaRecorderRef.current = null;
      setIsTranscribing(false);

      console.log('🛑 Transcription stopped');
    }
  }, []);

  // Listen for transcript updates from backend
  useEffect(() => {
    const socket = socketRef.current;
    if (!socket) return;

    const handleTranscriptUpdate = (data: {
      text: string;
      timestamp: string;
      speaker: string;
    }) => {
      console.log('📝 Received transcript:', data.text.substring(0, 50));

      setTranscript(prev => [...prev, {
        text: data.text,
        timestamp: data.timestamp,
        speaker: data.speaker
      }]);
    };

    socket.on('transcript-update', handleTranscriptUpdate);

    return () => {
      socket.off('transcript-update', handleTranscriptUpdate);
    };
  }, [socketRef]);

  // Auto-start/stop based on enabled flag
  useEffect(() => {
    console.log('🔄 Transcription enabled state changed:', { enabled, isTranscribing });

    if (enabled && !isTranscribing) {
      console.log('▶️ Starting transcription in 1 second...');
      // Small delay to ensure streams are ready
      const timer = setTimeout(() => {
        console.log('⏰ Transcription timer fired, calling startTranscription()');
        startTranscription();
      }, 1000);
      return () => {
        console.log('🧹 Clearing transcription start timer');
        clearTimeout(timer);
      };
    } else if (!enabled && isTranscribing) {
      console.log('⏹️ Stopping transcription...');
      stopTranscription();
    }
  }, [enabled, isTranscribing, startTranscription, stopTranscription]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopTranscription();
    };
  }, [stopTranscription]);

  return {
    transcript,
    isTranscribing,
    startTranscription,
    stopTranscription
  };
}
